import { TrashIcon } from 'src/domains/layouts/shared/assets/icons/TrashIcon';
import { WarningIcon } from 'src/domains/layouts/shared/assets/icons/WarningIcon';
import styled from '@emotion/styled';
import { withConfig } from 'src/withConfig';
import { Button } from 'src/domains/layouts/webview/components/common/button/Button';
import { DefaultComponentVariantType } from 'src/domains/layouts/config/features/types';
import { EmotionTheme } from 'src/domains/layouts/config/emotionTheme';
import { css, SerializedStyles } from '@emotion/react';

interface DesktopWrapperType {
    isFooter: boolean;
    variant: DefaultComponentVariantType | null;
}

const setYouDecideWrapper = (variant: DefaultComponentVariantType | null, theme: EmotionTheme): SerializedStyles => {
    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css`
            background-color: ${theme.star.rabSelectionList.bgColorTertiary};
            padding: ${theme.star.padding.medium};
            gap: ${theme.star.gap.medium};
        `;
    }
    return css``;
};

interface YouDecidePropsType {
    variant: DefaultComponentVariantType | null;
}

export const YouDecideWrapper = withConfig(theme => styled('div', { label: 'YouDecideWrapper' }) <DesktopWrapperType>`
    ${({ variant }): SerializedStyles => setYouDecideWrapper(variant, theme)}
    display: none;
    @media ${theme.star.mediaQuery.desktop} {
        background-color: ${theme.star.rabSelectionList.bgColor};
        bottom: 0;
        display: flex;
        flex-flow: column nowrap;
        left: 0;
        position: ${({ isFooter }):string => isFooter ? 'absolute' : 'fixed'};
        right: 280px;
        width: ${({ isFooter }):string => isFooter ? '100%' : 'initial'};
        ${theme.star.zIndexGlobal.above};
    }
    @media ${theme.star.mediaQuery.largeDesktop} {
        left: ${({ isFooter }):string => isFooter ? '0' : '200px'};
    }
`);


const setYouDecideContent = (variant: DefaultComponentVariantType | null, theme: EmotionTheme): SerializedStyles => {
    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css`
            display: flex;
            flex-flow: column nowrap;
            gap: ${theme.star.gap.medium};
        `;
    }
    return css``;
};

export const YouDecideContent = withConfig(theme => styled('div', { label: 'YouDecideContent' })<YouDecidePropsType>`
    flex-grow: 1;
    justify-content: center;
    position: initial;
    ${({ variant }): SerializedStyles => setYouDecideContent(variant, theme)}
`);

export const ContentConfirmationWrapper = withConfig(theme => styled('div', { label: 'ContentConfirmationWrapper' })`
    background-color: ${theme.star.statuses.success.bgColor};
    display: flex;
`);

export const ContentText = withConfig(theme => styled('p', { label: 'ContentText' })`
    background-color: ${theme.star.statuses.success.bgColor};
    color: ${theme.star.statuses.error.txtColor};
    font-size: ${theme.star.fontSize.small};
    line-height: 1.3334;
    margin: 0;
    padding: 4px;
    width: 100%;
`);

export const ErrorConflictWrapper = withConfig(theme => styled('div', { label: 'ErrorConflictWrapper' })<YouDecidePropsType>`
    background-color: ${theme.star.statuses.error.bgColor};
    color: ${theme.star.statuses.error.txtColor};
    line-height: 1;
    padding: 4px 4px 3px 28px;
    position: relative;
    border-radius: ${({ variant }): string => variant === DefaultComponentVariantType.SECONDARY ? theme.star.radius.small: '0'};
`);

export const ErrorConflictDesc = withConfig(theme => styled('p', { label: 'ErrorConflictDesc' })`
    background-color: ${theme.star.statuses.error.bgColor};
    color: ${theme.star.statuses.error.txtColor};
    font-size: ${theme.star.fontSize.small};
    line-height: 1.3334;
    margin: 0 2px 0 0;
    @media ${theme.star.mediaQuery.xxMobile} {
        display: inline;
    }
`);

export const RabWarningIconWrapper = styled(WarningIcon, { label: 'RabWarningIconWrapper' })`
    fill: currentcolor;
    position: absolute;
    left: 4px;
    top: 4px;
    width: 16px;
`;

interface YouDecideItemPropsType {
    isConflicting: boolean;
    variant: DefaultComponentVariantType | null;
}

const setYouDecideItem = (variant: DefaultComponentVariantType | null, theme: EmotionTheme): SerializedStyles => {
    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css`
            height: 44px;
            border-radius: ${theme.star.radius.medium};
            align-items: center;
            @media ${theme.star.mediaQuery.desktop}{
                flex: 1 1 165px;
            }
        `;
    }
    return css`
        height: 40px;
        &:first-of-type {
            margin-top: 4px;
            @media ${theme.star.mediaQuery.desktop} {
                margin-top: 0;
            }
        }
    `;
};

export const YouDecideItem = withConfig(theme => styled('li', { label: 'YouDecideItem' })<YouDecideItemPropsType>`
    ${({ variant }): SerializedStyles => setYouDecideItem(variant, theme)}
    background-color: ${theme.star.rabSelectionList.bgColorSecondary};
    box-shadow: inset 0 0 0 1px ${({ isConflicting }):string => isConflicting ? theme.star.statuses.error.bgColor : 'transparent'};
    color: ${theme.star.rabSelectionList.txtColor};
    display: flex;
    justify-content: space-between;
    min-width: 165px;
    padding: 4px 6px 6px 8px;
`);

export const YouDecideItemSelection = withConfig(theme => styled('span', { label: 'YouDecideItemSelection' })`
    display: block;
    font-size: ${theme.star.fontSize.small};
    line-height: 1.3334;
`);

export const YouDecideItemMarket = withConfig(theme => styled('span', { label: 'YouDecideItemMarket' })`
    display: block;
    font-size: ${theme.star.fontSize.xSmall};
    line-height: 1.4;
    opacity: 0.64;
`);

export const IconTrashWrapper = styled(TrashIcon, { label: 'IconTrashWrapper' })`
    fill: currentcolor;
    margin-left: 4px;
    width: 12px;
`;

export const DeleteItemIconTrashWrapper = styled(TrashIcon, { label: 'DeleteItemIconTrashWrapper' })<YouDecidePropsType>`
    align-self: ${({ variant }): string => variant === DefaultComponentVariantType.SECONDARY ? 'auto' : 'flex-start'};
    cursor: pointer;
    fill: currentcolor;
    margin-left: 4px;
    opacity: 0.8;
    width: 12px;
`;

const setYouDecideAddToBetSlip = (variant: DefaultComponentVariantType | null, theme: EmotionTheme): SerializedStyles => {
    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css`
            height: 56px;
        `;
    }
    return css`
        color: ${theme.star.rabSelectionList.txtColorSecondary};
    `;
};



export const YouDecideAddToBetSlip = withConfig(theme => styled(Button, { label: 'YouDecideAddToBetSlip' })<YouDecidePropsType>`
    ${({ variant }): SerializedStyles => setYouDecideAddToBetSlip(variant, theme)}
    background-color: ${theme.star.rabSelectionList.bgColorQuaternary};
    font-size: ${theme.star.fontSize.xRegular};
    line-height: 1.4286;
    padding: 2px 0;
    width: 100%;
    font-weight: ${theme.star.fontWeight.regular};
`);

export const ReturnsValue = withConfig(theme => styled('strong', { label: 'ReturnsValue' })`
    font-size: ${theme.star.fontSize.small};
    color: ${theme.star.rabSelectionList.txtColorTertiary};
    line-height: 1.5;
    &:first-of-type {
        margin-right: 4px;
    }
    &:last-of-type {
        margin-left: 4px;
    }
`);

export const AddToBetSlipCurrency = withConfig(theme => styled('strong', { label: 'AddToBetSlipCurrency' })<YouDecidePropsType>`
    font-size: ${({ variant }): string => variant === DefaultComponentVariantType.SECONDARY ? theme.star.fontSize.small : theme.star.fontSize.regular};
    line-height: 1.5;
    &:first-of-type {
        margin-right: 4px;
    }
    &:last-of-type {
        margin-left: 4px;
    }
`);


const setAddToBetSlipTxt = (variant: DefaultComponentVariantType | null, theme: EmotionTheme): SerializedStyles => {
    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css`
            font-weight: ${theme.star.fontWeight.bold};
            font-size: ${theme.star.fontSize.regular};
        `;
    }
    return css`
        font-size: ${theme.star.fontSize.xRegular};
    `;
};

export const AddToBetSlipTxt = withConfig(theme => styled('p', { label: 'AddToBetSlipTxt' })<YouDecidePropsType>`
    ${({ variant }): SerializedStyles => setAddToBetSlipTxt(variant, theme)}
    line-height: 1.4286;
    margin: 0;
`);

export const YouDecideReturnsTxt = withConfig(theme => styled('p', { label: 'YouDecideReturnsTxt' })<YouDecidePropsType>`
    font-size: ${({ variant }): string => variant === DefaultComponentVariantType.SECONDARY ? theme.star.fontSize.small : theme.star.fontSize.regular};
    line-height: 1.4286;
    margin: 0;
`);

const setYouDecideList = (variant: DefaultComponentVariantType | null, theme: EmotionTheme): SerializedStyles => {
    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css`
            display: flex;
            flex-flow: column nowrap;
            flex-grow: 0;
            flex-shrink: 0;
            gap: ${theme.star.gap.medium};
            @media ${theme.star.mediaQuery.desktop} {
                flex-flow: row nowrap;
            }
        `;
    }
    return css`
        display: grid;
        gap: ${theme.star.gap.small};
        grid-template-columns: repeat(3, 1fr);
        padding: ${theme.star.padding.medium};
        @media ${theme.star.mediaQuery.desktop} {
            gap: ${theme.star.gap.medium};
        }
        @media ${theme.star.mediaQuery.xxLargeDesktop} {
            grid-template-columns: repeat(4, 1fr);
        }
    `;
};

export const YouDecideList = withConfig(theme => styled('ul', { label: 'YouDecideList' })<YouDecidePropsType>`
    max-height: 153px;
    overflow-y: auto;
    ${({ variant }): SerializedStyles => setYouDecideList(variant, theme)}
`);

const setYouDecideMore = (variant: DefaultComponentVariantType | null, theme: EmotionTheme): SerializedStyles => {
    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css`
            @media ${theme.star.mediaQuery.desktop} {
                flex: 1 1 165px;
            }
            min-width: 165px;
            height: 44px;
            border-radius: ${theme.star.radius.medium};
        `;
    }
    return css``;
};

export const YouDecideMore = withConfig(theme => styled('li', { label: 'YouDecideMore' })<YouDecidePropsType>`
    --border-color: ${theme.star.rabSelectionList.borderColor};
    --linear-gradient: var(--border-color),
        var(--border-color) 9px,
        transparent 9px,
        transparent 18px,
        var(--border-color) 18px;

    background-image: repeating-linear-gradient(0deg, var(--linear-gradient)),
        repeating-linear-gradient(90deg, var(--linear-gradient)),
        repeating-linear-gradient(180deg, var(--linear-gradient)),
        repeating-linear-gradient(270deg, var(--linear-gradient));

    align-items: center;
    background-position: 0 0, 0 0, 100% 0, 0 100%;
    background-repeat: no-repeat;
    background-size: 1px 100%,
        100% 1px,
        1px 100%,
        100% 1px;
    ${({ variant }): SerializedStyles => setYouDecideMore(variant, theme)}
    color: ${theme.star.rabSelectionList.txtColor};
    display: flex;
    font-size: ${theme.star.fontSize.small};
    justify-content: center;
    line-height: 1.3334;
    margin: 0 0 4px;
    padding: 12px;
    &:only-of-type {
        margin: 0;
    }
    @media ${theme.star.mediaQuery.desktop} {
        margin-bottom: 0;
    }
`);

const setYouDecideListHeader = (variant: DefaultComponentVariantType | null, theme: EmotionTheme): SerializedStyles => {
    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css``;
    }
    return css`
        height: 24px;
        padding: 0 ${theme.star.padding.medium};
    `;
};

export const YouDecideListHeader = withConfig(theme => styled('div', { label: 'YouDecideListHeader' })<YouDecidePropsType>`
    ${({ variant }): SerializedStyles => setYouDecideListHeader(variant, theme)}
    align-items: center;
    background-color: ${theme.star.rabSelectionList.bgColorTertiary};
    display: flex;
    justify-content: space-between;
    &:empty {
        display: none;
    }
`);

const setYouDecideListHeaderCount = (variant: DefaultComponentVariantType | null, theme: EmotionTheme): SerializedStyles => {
    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css`
            font-size: ${theme.star.fontSize.regular};
            font-weight: ${theme.star.fontWeight.bold};
            line-height: 1.5;
            text-transform: capitalize;
        `;
    }
    return css`
        font-size: ${theme.star.fontSize.small};
        line-height: 1.3334;
    `;
};


export const YouDecideListHeaderCount = withConfig(theme => styled('div', { label: 'YouDecideListHeaderCount' })<YouDecidePropsType>`
    ${({ variant }): SerializedStyles => setYouDecideListHeaderCount(variant, theme)}
    color: ${theme.star.rabSelectionList.txtColor};
`);

interface PropsType {
    onClick: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
}

export const YouDecideListHeaderRemove = withConfig(theme => styled('div', { label: 'YouDecideListHeaderRemove' })<PropsType>`
    align-items: center;
    color: ${theme.star.rabSelectionList.txtColor};
    cursor: pointer;
    display: flex;
    font-size: ${theme.star.fontSize.small};
    font-weight: ${theme.star.fontWeight.bold};
    line-height: 1.3334;
    opacity: 0.8;
`);
